/**
 * fetch() wrapper that routes through tauri-plugin-http when the app
 * runs inside the Tauri Windows wrapper, and through the regular
 * browser fetch everywhere else.
 *
 * Why: Tauri 2's webview origin is http://tauri.localhost on
 * Windows/Linux (tauri://localhost on macOS). Third-party auth servers
 * — including the game's Patreon auth backend — almost never list
 * those in their Access-Control-Allow-Origin, so a plain fetch fails
 * at the CORS preflight and the request never reaches the server. The
 * plugin performs the request with Rust reqwest instead, completely
 * outside the webview's CORS pipeline.
 *
 * The plugin module is loaded lazily through a dynamic import so
 * web/PWA/Android builds don't pay its bundle cost — CRA emits a
 * separate chunk that is only fetched when we actually detect
 * window.__TAURI__.
 */

let _tauriFetchPromise = null;

function loadTauriFetch() {
  if (_tauriFetchPromise) return _tauriFetchPromise;
  _tauriFetchPromise = import('@tauri-apps/plugin-http')
    .then((m) => m.fetch)
    .catch((err) => {
      // Dynamic import failed — probably the plugin isn't available
      // at runtime for some reason. Clear the cache so a later call
      // can retry, and propagate so smartFetch falls back.
      _tauriFetchPromise = null;
      throw err;
    });
  return _tauriFetchPromise;
}

export async function smartFetch(input, init) {
  if (typeof window !== 'undefined' && window.__TAURI__) {
    try {
      const tfetch = await loadTauriFetch();
      return await tfetch(input, init);
    } catch (err) {
      console.warn(
        '[smartFetch] tauri-plugin-http fetch failed, falling back to window.fetch:',
        err && err.message ? err.message : err
      );
      // Fall through to window.fetch — at least on dev (e.g. running
      // the PWA in a normal browser with __TAURI__ injected) this
      // gives a meaningful error instead of a cryptic IPC one.
    }
  }
  return fetch(input, init);
}
